<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import StemneButton from '@/components/ui/StemneButton.vue'
import StemneModal from '@/components/ui/StemneModal.vue'

// Confirm-action modal — composes StemneModal with a destructive-by-default
// tone, a body paragraph, and Cancel/Confirm buttons. Cancel is auto-
// focused so a stray Enter doesn't blow away data.
const props = withDefaults(
  defineProps<{
    show: boolean
    title: string
    body?: string
    confirmLabel: string
    cancelLabel?: string
    variant?: 'destructive' | 'neutral'
    loading?: boolean
    // Holds the confirm button dead while a precondition rendered in the
    // default slot is unmet — the angrerett § 12 toggle on the reschedule
    // confirms is the first caller. Distinct from `loading`: no spinner, and
    // cancel stays live so the customer is never trapped in the dialog.
    confirmDisabled?: boolean
  }>(),
  {
    variant: 'destructive',
    loading: false,
    confirmDisabled: false,
  },
)

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const { t } = useI18n()

// The cancel label defaults to the shared "Avbryt"/"Cancel" catalog string, not
// a hardcoded Norwegian literal, so English-mode operators don't see "Avbryt"
// next to an otherwise-English dialog (#2375). Callers can still override it.
const cancelText = computed(() => props.cancelLabel ?? t('common.abort'))

// StemneButton is a component, so the template ref resolves to its
// public instance, not a DOM node — reach the underlying <button>
// via $el to actually move focus. (Calling .focus() on the instance
// throws "focus is not a function" in a real browser.)
const cancelBtn = useTemplateRef<{ $el?: HTMLElement }>('cancelBtn')

// Focus Cancel every time the modal OPENS, not just at mount (#2374). Almost
// every caller keeps this component mounted and toggles `show`, so at mount the
// footer button doesn't exist yet (StemneModal only renders its footer while
// open) and an onMounted-only focus was a dead no-op for them. nextTick waits
// for StemneModal to render the footer; the rAF defers past the parent's own
// card-focus so we don't fight it. `immediate` covers the rarer created-open
// caller (StemneModal's internal discard prompt).
watch(
  () => props.show,
  (isOpen) => {
    if (!isOpen) return
    nextTick(() => requestAnimationFrame(() => cancelBtn.value?.$el?.focus()))
  },
  { immediate: true },
)
</script>

<template>
  <StemneModal
    :show="show"
    :title="title"
    :description="body"
    width="sm"
    :tone="variant === 'destructive' ? 'destructive' : 'neutral'"
    :show-close-button="false"
    :locked="loading"
    @close="emit('cancel')"
  >
    <!-- aria-hidden, deliberately: `body` is passed to StemneModal as the
         dialog's `description`, which renders it again for assistive tech and
         wires aria-describedby to it. Without this the same sentence is in the
         tree twice — announced on open as the description, then read again as
         page content — on every confirm dialog in the app. Sighted users see
         this copy; screen readers get the other one, once. -->
    <p v-if="body" class="confirm-body" aria-hidden="true">{{ body }}</p>
    <!-- Optional extra content below the body (e.g. a late-change note on
         the reschedule confirm, #2290). Renders nothing when no slot is
         passed, so existing callers are unaffected. -->
    <slot />

    <template #footer>
      <StemneButton
        ref="cancelBtn"
        variant="quiet"
        type="button"
        :disabled="loading"
        @click="emit('cancel')"
      >
        {{ cancelText }}
      </StemneButton>
      <StemneButton
        :variant="variant === 'destructive' ? 'destructive' : 'primary'"
        type="button"
        :loading="loading"
        :disabled="loading || confirmDisabled"
        @click="emit('confirm')"
      >
        {{ confirmLabel }}
      </StemneButton>
    </template>
  </StemneModal>
</template>

<style lang="scss" scoped>
.confirm-body {
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: var(--fg-muted);
  margin: 0;
}
</style>
